import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useAuth } from '../auth/AuthProvider';
import {
  useCreateJobMutation,
  useGetCreditsBalanceQuery,
  useGetJobQuery,
  useLazyGetEmbedPendingQuery,
  useListPresetsQuery,
  useStemsFromUploadMutation,
  PresetDoc,
} from '../redux/services/mixgenie';

/**
 * ComposerPage — the redesigned generator.
 *
 * Mounted at /composer as a standalone route (no AppShell) so it owns
 * the entire viewport, the way the pictools composer does. The idea in
 * one line: **one prompt textarea, one gradient send button with the
 * credit price stamped inside it, everything else optional as a chip**.
 *
 * P2 scope (this file): the visual shell + local state + the three
 * view modes (empty / generating / result). Preset picking, reference
 * upload, and real generation are wired in later phases (P3, P4) —
 * the placeholders here fire a fake 3-second "generating" then swap
 * to a canned result so the shape can be reviewed end-to-end.
 *
 * Legacy /generator (GeneratorPage.tsx) is untouched; App.tsx routes
 * BOTH URLs so a rollback is one line if the composer is the wrong
 * direction.
 */

// ---------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------

const MG_GRADIENT = 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)';
const MG_GRADIENT_FLAT = 'linear-gradient(90deg, #ec4899 0%, #a855f7 100%)';

type ViewMode = 'empty' | 'generating' | 'result';

// -----------------------------------------------------------------------
// Reference-audio helpers — ported verbatim from the legacy GeneratorPage
// so the composer submits the same shape the backend + Mureka expect. See
// GeneratorPage.tsx for the original prose comments; the two rules that
// matter operationally are (1) ASCII-only filename or PromptLab's S3
// SigV4 breaks, (2) head-clip to ~8 MB, Mureka only reads the first ~60 s.
// -----------------------------------------------------------------------

const REFERENCE_MAX_BYTES = 8 * 1024 * 1024;

const LANG_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese',
};

function sanitizeReferenceFileName(name: string, mime: string): string {
  const ext = mime === 'audio/mpeg' ? '.mp3'
    : mime === 'audio/mp4' ? '.m4a'
    : mime === 'audio/wav' ? '.wav'
    : mime === 'audio/ogg' ? '.ogg'
    : /\.\w{2,4}$/.test(name) ? name.slice(name.lastIndexOf('.')) : '.mp3';
  const stem = (name || 'reference')
    .replace(/\.[^.]+$/, '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\x20-\x7E]+/g, '')
    .replace(/[<>:"|?*\\/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60) || 'reference';
  return `${stem}${ext}`;
}

async function fileToReferenceAudio(file: File): Promise<{ fileName: string; mimeType: string; base64: string; size: number }> {
  let buf = await file.arrayBuffer();
  if (buf.byteLength > REFERENCE_MAX_BYTES) buf = buf.slice(0, REFERENCE_MAX_BYTES);
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  const mimeType = file.type || 'audio/mpeg';
  return {
    fileName: sanitizeReferenceFileName(file.name, mimeType),
    mimeType,
    base64: btoa(binary),
    size: bytes.length,
  };
}

// Render N vertical bars synthesised from an audio URL by seeding a
// deterministic PRNG from its length. Not a real waveform — a real one
// needs the buffer decoded via WebAudio — but stable per-track so the
// result stage doesn't twitch on rerender. Real waveform is a P4 follow-up.
function synthWaveform(seed = 42, count = 60): number[] {
  const bars: number[] = [];
  let s = seed;
  for (let i = 0; i < count; i++) {
    s = (s * 9301 + 49297) % 233280;
    const rand = s / 233280;
    bars.push(0.25 + rand * 0.75);
  }
  return bars;
}

// ---------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------

// -----------------------------------------------------------------------
// Nav drawer icons
//
// Inline SVGs from a Lucide-style stroke set — 20px, 1.75 stroke, round
// caps/joins. Rendered inside a 40×40 tinted tile (see NavItem below).
// Original glyph choices; not derived from any third-party icon pack's
// artwork beyond the general "hairline outline" idiom.
// -----------------------------------------------------------------------

const IconWand = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 4V2M15 10V8M9 3h1M18 6h1M13 6l6 6L6 21l-3-3L13 6Z" />
    <path d="M14 7l3 3" opacity=".6" />
  </svg>
);

const IconMusic = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V6l12-2v12" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const IconHeart = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z" />
  </svg>
);

const IconCoins = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6" />
    <path d="M18.09 10.37A6 6 0 1 1 10.34 18M7 6h1v4M16.71 13.88l.7.71-2.82 2.82" />
  </svg>
);

const IconHelp = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
  </svg>
);

const IconGear = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);

const IconSun = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const IconMoon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
  </svg>
);

const IconLogout = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
);

// -----------------------------------------------------------------------
// Nav item — icon tile + label
// -----------------------------------------------------------------------

function NavItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      display="flex"
      alignItems="center"
      gap={3.5}
      w="100%"
      textAlign="left"
      px={2.5}
      py={2}
      borderRadius="12px"
      color="mg.fg"
      fontSize="15px"
      fontWeight={700}
      letterSpacing="-0.005em"
      _hover={{ bg: 'mg.surfaceHi' }}
      transition="background-color .12s"
      onClick={onClick}
    >
      <Box
        w="40px"
        h="40px"
        flex="0 0 auto"
        display="grid"
        placeItems="center"
        bg="hot.50"
        color="hot.500"
        borderRadius="12px"
        _dark={{ bg: 'rgba(236,72,153,0.14)', color: 'hot.300' }}
      >
        {icon}
      </Box>
      <Text as="span">{label}</Text>
    </Box>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Text
      fontSize="11px"
      textTransform="uppercase"
      letterSpacing="0.12em"
      fontWeight={700}
      color="mg.muted"
      px={2.5}
      mt={5}
      mb={2}
    >
      {children}
    </Text>
  );
}

function NavDrawer({
  isOpen,
  onClose,
  authenticated,
  email,
  credits,
  onSignOut,
  onOpenSettings,
}: {
  isOpen: boolean;
  onClose: () => void;
  authenticated: boolean;
  email: string;
  credits: number | null;
  onSignOut: () => void;
  onOpenSettings: () => void;
}) {
  const navigate = useNavigate();
  const initial = (email[0] || 'U').toUpperCase();

  const go = (to: string) => () => { onClose(); navigate(to); };
  const external = (href: string) => () => { onClose(); window.open(href, '_blank', 'noopener,noreferrer'); };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="left" size="xs" autoFocus={false}>
      <DrawerOverlay bg="blackAlpha.500" />
      <DrawerContent bg="mg.surface" color="mg.fg" boxShadow="0 20px 60px -20px rgba(0,0,0,0.35)">
        <DrawerCloseButton color="mg.muted" top="14px" right="14px" size="md" _hover={{ bg: 'mg.surfaceHi' }} />

        {/* Header (brand only — the close button is the top-right ✕) */}
        <Box px={4} pt={4} pb={2}>
          <Text
            fontFamily="'Iowan Old Style', Charter, Georgia, serif"
            fontWeight={500}
            fontSize="22px"
            letterSpacing="-0.02em"
          >
            mix
            <Box
              as="em"
              fontStyle="normal"
              fontWeight={700}
              bgGradient={MG_GRADIENT_FLAT}
              bgClip="text"
              sx={{ WebkitBackgroundClip: 'text', color: 'transparent' }}
            >
              GENIE
            </Box>
          </Text>
        </Box>

        {/* Main scrollable area */}
        <DrawerBody px={2} pt={2} pb={2} display="flex" flexDirection="column">
          <Box display="flex" flexDirection="column" gap={0.5}>
            <NavItem icon={<IconWand />}  label="New song"       onClick={go('/composer')} />
            <NavItem icon={<IconMusic />} label="My songs"       onClick={go('/songs')} />
            <NavItem icon={<IconHeart />} label="Favourites"     onClick={go('/favourites')} />
            <NavItem icon={<IconCoins />} label="Credits"        onClick={go('/credits')} />
            <NavItem icon={<IconHelp />}  label="Support"        onClick={external('https://help.mixgenie.ai')} />
          </Box>

          {/* Sections for later — favourites/recent presets go here when
              P3 wires the real preset catalog. Kept as an empty scaffold
              so the shape is visible now. */}
          <SectionHeader>Favourite presets</SectionHeader>
          <Text px={2.5} fontSize="12.5px" color="mg.dim" lineHeight="1.5">
            Save a preset from the picker to see it here.
          </Text>

          <SectionHeader>Recent presets</SectionHeader>
          <Text px={2.5} fontSize="12.5px" color="mg.dim" lineHeight="1.5">
            The last presets you tried appear here.
          </Text>

          <Box flex="1" />
        </DrawerBody>

        {/* Sticky account bar at the bottom — avatar · name · credits · gear */}
        <Box
          borderTop="1px solid"
          borderColor="mg.line"
          px={3}
          py={3}
          display="flex"
          alignItems="center"
          gap={3}
          bg="mg.surface"
        >
          {authenticated ? (
            <>
              <Box
                w="40px"
                h="40px"
                borderRadius="full"
                bgGradient={MG_GRADIENT}
                display="grid"
                placeItems="center"
                color="white"
                fontFamily="'Iowan Old Style', Charter, Georgia, serif"
                fontSize="17px"
                fontWeight={700}
                flex="0 0 auto"
              >
                {initial}
              </Box>
              <Box minW={0} flex="1">
                <Text fontSize="14px" fontWeight={700} color="mg.fg" noOfLines={1}>
                  {email ? email.split('@')[0] : 'You'}
                </Text>
                <Text fontSize="12px" color="mg.muted" mt="1px">
                  {credits ?? '…'} credits
                </Text>
              </Box>
              <Box
                as="button"
                w="36px"
                h="36px"
                display="grid"
                placeItems="center"
                borderRadius="10px"
                bg="hot.50"
                color="hot.500"
                _dark={{ bg: 'rgba(236,72,153,0.14)', color: 'hot.300' }}
                _hover={{ opacity: 0.85 }}
                onClick={onOpenSettings}
                aria-label="Settings"
              >
                <IconGear />
              </Box>
            </>
          ) : (
            <>
              <Box
                w="40px"
                h="40px"
                borderRadius="full"
                bg="mg.surfaceHi"
                color="mg.muted"
                display="grid"
                placeItems="center"
                fontSize="20px"
                flex="0 0 auto"
              >
                ○
              </Box>
              <Box minW={0} flex="1">
                <Text fontSize="14px" fontWeight={700} color="mg.fg">Not signed in</Text>
                <Text fontSize="12px" color="mg.muted" mt="1px">Try 2 songs free</Text>
              </Box>
              <Box
                as="button"
                px={3}
                py={2}
                borderRadius="10px"
                bg={MG_GRADIENT}
                color="white"
                fontSize="12.5px"
                fontWeight={700}
                onClick={() => { onClose(); navigate('/login?next=/composer'); }}
              >
                Sign in
              </Box>
            </>
          )}
        </Box>
      </DrawerContent>
    </Drawer>
  );
}

// -----------------------------------------------------------------------
// Settings sheet — theme + sign out. Opened from the drawer's gear.
// -----------------------------------------------------------------------

function SettingsSheet({
  isOpen,
  onClose,
  authenticated,
  onSignOut,
}: {
  isOpen: boolean;
  onClose: () => void;
  authenticated: boolean;
  onSignOut: () => void;
}) {
  const { colorMode, toggleColorMode } = useColorMode();
  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" size="xs" autoFocus={false}>
      <DrawerOverlay bg="blackAlpha.500" />
      <DrawerContent
        bg="mg.surface"
        color="mg.fg"
        borderTopRadius="18px"
        maxH="60vh"
      >
        <Box textAlign="center" pt={3}>
          <Box mx="auto" w="40px" h="4px" bg="mg.dim" borderRadius="2px" />
        </Box>
        <DrawerHeader
          fontFamily="'Iowan Old Style', Charter, Georgia, serif"
          fontWeight={500}
          fontSize="18px"
          letterSpacing="-0.01em"
          pt={2}
          pb={2}
        >
          Settings
        </DrawerHeader>
        <DrawerBody px={3} pb={5} display="flex" flexDirection="column" gap={1}>
          <NavItem
            icon={colorMode === 'dark' ? <IconSun /> : <IconMoon />}
            label={colorMode === 'dark' ? 'Light theme' : 'Dark theme'}
            onClick={toggleColorMode}
          />
          {authenticated && (
            <NavItem
              icon={<IconLogout />}
              label="Sign out"
              onClick={() => { onClose(); onSignOut(); }}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function TopBar({
  authenticated,
  credits,
  onSignInClick,
  onMenuClick,
}: {
  authenticated: boolean;
  credits: number | null;
  onSignInClick: () => void;
  onMenuClick: () => void;
}) {
  return (
    <Flex
      as="header"
      align="center"
      gap={2}
      px={3}
      py={2.5}
      borderBottom="1px solid"
      borderColor="mg.line"
      bg="mg.surface"
      position="sticky"
      top={0}
      zIndex={5}
    >
      {/* Menu tile — pink-tinted square, matches the drawer's nav
          icon tiles so it feels like part of the same system. */}
      <Box
        as="button"
        w="40px"
        h="40px"
        flex="0 0 auto"
        display="grid"
        placeItems="center"
        bg="hot.50"
        color="hot.500"
        _dark={{ bg: 'rgba(236,72,153,0.14)', color: 'hot.300' }}
        borderRadius="10px"
        onClick={onMenuClick}
        aria-label="Menu"
      >
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M1 1h16M1 7h16M1 13h16" />
        </svg>
      </Box>
      <Text
        as={Link}
        to="/composer"
        flex="1"
        fontSize="20px"
        fontWeight={600}
        letterSpacing="-0.02em"
        fontFamily="'Iowan Old Style', Charter, Georgia, serif"
        color="mg.fg"
      >
        mix
        <Box
          as="em"
          fontStyle="normal"
          fontWeight={700}
          bgGradient={MG_GRADIENT_FLAT}
          bgClip="text"
          sx={{ WebkitBackgroundClip: 'text', color: 'transparent' }}
        >
          GENIE
        </Box>
      </Text>
      {authenticated ? (
        <HStack spacing={1.5} bg="mg.surfaceHi" color="mg.muted" fontSize="11.5px" fontWeight={700} px={2.5} py={1.5} borderRadius="full" border="1px solid" borderColor="mg.line">
          <Box w="6px" h="6px" borderRadius="full" bgGradient={MG_GRADIENT} />
          <Text as="span">{credits ?? '…'} credits</Text>
        </HStack>
      ) : (
        <Box
          as="button"
          fontSize="12px"
          fontWeight={700}
          color="mg.fg"
          px={3}
          py={2}
          borderRadius="full"
          bg="mg.surfaceHi"
          border="1px solid"
          borderColor="mg.line2"
          onClick={onSignInClick}
        >
          Sign in · 2 free
        </Box>
      )}
    </Flex>
  );
}

function DropCard({
  onTypeIdea,
  onUploadReference,
  onPickPreset,
  onSplitStems,
}: {
  onTypeIdea: () => void;
  onUploadReference: () => void;
  onPickPreset: () => void;
  onSplitStems: (stemModel: 'audio-separation-2' | 'audio-separation-1' | 'audio-separation-3') => void;
}) {
  return (
    <Box
      bg="mg.surface"
      border="1.5px dashed"
      borderColor="mg.line2"
      borderRadius="22px"
      p="32px 20px 26px"
      textAlign="center"
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={2}
    >
      <Box
        w="56px"
        h="56px"
        borderRadius="16px"
        bg="linear-gradient(135deg, rgba(236,72,153,0.15), rgba(168,85,247,0.15))"
        border="1px solid rgba(236,72,153,0.25)"
        display="grid"
        placeItems="center"
        fontSize="28px"
        mb={1}
      >
        🎵
      </Box>
      <Text
        fontFamily="'Iowan Old Style', Charter, Georgia, serif"
        fontWeight={500}
        fontSize="20px"
        letterSpacing="-0.01em"
        m={0}
        color="mg.fg"
      >
        Create a song
      </Text>
      <Text fontSize="12.5px" color="mg.muted" maxW="26ch" m={0}>
        Start with an idea, a reference, or a preset.
      </Text>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} w="100%" mt={3}>
        <DropButton icon="✍️" label="Type an idea" onClick={onTypeIdea} />
        <DropButton icon="🎧" label="Upload reference" onClick={onUploadReference} />
        <DropButton icon="✨" label="Pick a preset" onClick={onPickPreset} />
        {/* Split-stems tile is a Menu instead of a plain button so the
            user picks the split depth (2 / 5 / 12 stems) BEFORE the file
            dialog opens — one click, no popover round-trip. */}
        <Menu placement="bottom-end" strategy="fixed">
          <MenuButton
            as={Box}
            role="button"
            cursor="pointer"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            gap={2}
            px={3}
            py={3}
            bg="mg.surfaceHi"
            color="mg.fg"
            border="1px solid"
            borderColor="mg.line2"
            borderRadius="12px"
            fontSize="13.5px"
            fontWeight={600}
            _hover={{ bg: 'mg.surface' }}
          >
            <Box as="span" fontSize="15px" aria-hidden="true">🎛</Box>
            <Box as="span">Split stems ▾</Box>
          </MenuButton>
          <MenuList bg="mg.surface" borderColor="mg.line2" minW="260px" zIndex={2000}>
            <MenuItem
              bg="mg.surface"
              color="mg.fg"
              _hover={{ bg: 'mg.surfaceHi' }}
              _focus={{ bg: 'mg.surfaceHi' }}
              onClick={() => onSplitStems('audio-separation-2')}
            >
              <VStackTight main="12 stems (full)" sub="vocals · drums · bass · guitar · piano · strings…  + MIDI" />
            </MenuItem>
            <MenuItem
              bg="mg.surface"
              color="mg.fg"
              _hover={{ bg: 'mg.surfaceHi' }}
              _focus={{ bg: 'mg.surfaceHi' }}
              onClick={() => onSplitStems('audio-separation-1')}
            >
              <VStackTight main="5 stems (classic)" sub="instrumental · vocals · drums · bass · other" />
            </MenuItem>
            <MenuItem
              bg="mg.surface"
              color="mg.fg"
              _hover={{ bg: 'mg.surfaceHi' }}
              _focus={{ bg: 'mg.surfaceHi' }}
              onClick={() => onSplitStems('audio-separation-3')}
            >
              <VStackTight main="2 stems (karaoke)" sub="vocals · accompaniment  + MIDI" />
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
      <Text mt={3} fontSize="12px" color="mg.dim">
        or just type below — it's fine to start with nothing.
      </Text>
    </Box>
  );
}

// Small two-line label used inside MenuItem cells so the menu can carry
// a short primary line + a subdued explanation without leaning on Chakra
// composition tricks that don't nest cleanly inside MenuItem.
function VStackTight({ main, sub }: { main: string; sub: string }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" lineHeight={1.25}>
      <Box as="span" fontSize="13.5px" fontWeight={600}>{main}</Box>
      <Box as="span" fontSize="11.5px" color="mg.muted">{sub}</Box>
    </Box>
  );
}

function DropButton({
  icon,
  label,
  onClick,
  full = false,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  full?: boolean;
}) {
  return (
    <Box
      as="button"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap={2}
      px={3}
      py={3}
      bg="mg.surfaceHi"
      color="mg.fg"
      border="1px solid"
      borderColor="mg.line2"
      borderRadius="12px"
      fontSize="13.5px"
      fontWeight={600}
      gridColumn={full ? '1 / -1' : 'auto'}
      onClick={onClick}
    >
      <Box as="span" fontSize="15px" aria-hidden="true">{icon}</Box>
      <Box as="span">{label}</Box>
    </Box>
  );
}

function GeneratingStage() {
  // Nine-tile mosaic pop — synthetic decoration, not a mimic of any
  // trademarked logo. Just a pleasant pulse pattern.
  return (
    <Flex direction="column" align="center" justify="center" gap={3.5} py="40px" px={5} textAlign="center">
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        gridTemplateRows="repeat(3, 1fr)"
        gap="4px"
        w="96px"
        h="96px"
        aria-hidden="true"
      >
        {[...Array(9)].map((_, i) => (
          <Box
            key={i}
            bgGradient={MG_GRADIENT}
            borderRadius="4px"
            sx={{
              opacity: 0.25,
              animation: `mgPop 1.4s ease-in-out infinite`,
              animationDelay: `${i * 0.08}s`,
              '@keyframes mgPop': {
                '0%, 100%': { opacity: 0.25, transform: 'scale(1)' },
                '40%': { opacity: 1, transform: 'scale(1.15)' },
              },
            }}
          />
        ))}
      </Box>
      <Text
        fontFamily="'Iowan Old Style', Charter, Georgia, serif"
        fontWeight={500}
        fontSize="18px"
        letterSpacing="-0.01em"
        color="mg.fg"
      >
        Composing your song…
      </Text>
      <Text fontSize="12.5px" color="mg.muted" m={0}>
        Lyrics · vocals · arrangement · cover art. Usually 2–4 min.
      </Text>
    </Flex>
  );
}

function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function ResultStage({
  title,
  style,
  coverImageUrl,
  audioUrl,
  onGenerateAnother,
  onDownload,
  onOpenStudio,
}: {
  title: string;
  style: string;
  coverImageUrl?: string;
  audioUrl?: string;
  onGenerateAnother: () => void;
  onDownload: () => void;
  onOpenStudio?: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);   // 0..1
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const waveform = useMemo(() => synthWaveform((audioUrl || title || 'x').length || 42, 60), [audioUrl, title]);
  const playedTo = Math.floor(waveform.length * (progress || 0));

  useEffect(() => {
    // New audio arrived (post-generation swap) — reset scrub state.
    setProgress(0);
    setCurrentTime(0);
    setPlaying(false);
  }, [audioUrl]);

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); return; }
    a.play().catch(() => { /* browser blocked autoplay — user needs to interact */ });
  }

  function seekTo(fraction: number) {
    const a = audioRef.current;
    if (!a || !isFinite(a.duration) || a.duration <= 0) return;
    a.currentTime = Math.max(0, Math.min(a.duration, fraction * a.duration));
  }

  const cover = coverImageUrl
    ? <Box as="img" src={coverImageUrl} alt="" w="88px" h="88px" borderRadius="14px" flex="0 0 auto" objectFit="cover" boxShadow="0 8px 24px -8px rgba(168,85,247,0.55)" />
    : <Box w="88px" h="88px" borderRadius="14px" flex="0 0 auto" background="radial-gradient(circle at 30% 30%, #fbcfe8 0%, transparent 45%), radial-gradient(circle at 70% 60%, #c4b5fd 0%, transparent 50%), linear-gradient(135deg, #a855f7, #ec4899)" boxShadow="0 8px 24px -8px rgba(168,85,247,0.55)" />;

  return (
    <Box
      bg="linear-gradient(180deg, var(--chakra-colors-mg-surfaceHi), var(--chakra-colors-mg-surface))"
      border="1px solid"
      borderColor="mg.line"
      borderRadius="22px"
      p={4}
      display="flex"
      flexDirection="column"
      gap={3}
    >
      <Flex justify="space-between" fontSize="10.5px" textTransform="uppercase" letterSpacing="0.12em" fontWeight={700} color="mg.muted">
        <Text>Just generated</Text>
        <Text color="mg.muted" textTransform="none" letterSpacing="0" fontWeight={500}>{fmtTime(duration)}</Text>
      </Flex>
      <Flex gap={3.5} align="center">
        {cover}
        <Box minW={0} flex="1">
          <Text
            fontFamily="'Iowan Old Style', Charter, Georgia, serif"
            fontWeight={500}
            fontSize="17px"
            letterSpacing="-0.01em"
            color="mg.fg"
            noOfLines={1}
          >
            {title}
          </Text>
          <Text fontSize="12px" color="mg.muted" mt="2px" noOfLines={1}>{style} · Full song</Text>
          <HStack spacing={1.5} mt={2.5}>
            <Box
              as="button"
              bg={MG_GRADIENT}
              color="white"
              border={0}
              px={3}
              py={1.5}
              borderRadius="8px"
              fontSize="12.5px"
              fontWeight={700}
              boxShadow="0 6px 16px -6px rgba(236,72,153,0.55)"
              onClick={togglePlay}
              disabled={!audioUrl}
            >
              {playing ? '❚❚ Pause' : '▶ Play'}
            </Box>
            <Box as="button" bg="mg.surface" color="mg.fg" border="1px solid" borderColor="mg.line2" px={3} py={1.5} borderRadius="8px" fontSize="12.5px" fontWeight={600} onClick={onDownload} disabled={!audioUrl}>
              ↓ Download
            </Box>
            {onOpenStudio && (
              <Box as="button" bg="mg.surface" color="mg.fg" border="1px solid" borderColor="mg.line2" px={3} py={1.5} borderRadius="8px" fontSize="12.5px" fontWeight={600} onClick={onOpenStudio} disabled={!audioUrl}>
                🎛 Studio
              </Box>
            )}
          </HStack>
        </Box>
      </Flex>
      {/* Clickable waveform — tap to seek. */}
      <Box
        as="button"
        h="40px"
        display="flex"
        alignItems="end"
        gap="2px"
        py="4px"
        w="100%"
        cursor="pointer"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          seekTo((e.clientX - rect.left) / rect.width);
        }}
        aria-label="Seek"
      >
        {waveform.map((h, i) => (
          <Box
            key={i}
            flex="1"
            h={`${h * 100}%`}
            bgGradient="linear(to-b, #ec4899, #a855f7)"
            borderRadius="2px"
            opacity={i < playedTo ? 1 : 0.35}
            pointerEvents="none"
          />
        ))}
      </Box>
      <Flex fontSize="11px" color="mg.muted">
        <Text>{fmtTime(currentTime)}</Text>
        <Text ml="auto">{fmtTime(duration)}</Text>
      </Flex>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} mt={1}>
        <Box
          as="button"
          bg={MG_GRADIENT}
          color="white"
          border={0}
          py={2.5}
          borderRadius="10px"
          fontSize="13px"
          fontWeight={700}
          boxShadow="0 6px 16px -6px rgba(236,72,153,0.55)"
          onClick={onGenerateAnother}
        >
          ⚡ Generate another
        </Box>
        <DropButton icon="♫" label="My songs" onClick={() => window.location.assign('/songs')} />
      </Box>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onLoadedMetadata={(e) => setDuration((e.target as HTMLAudioElement).duration || 0)}
          onTimeUpdate={(e) => {
            const a = e.target as HTMLAudioElement;
            setCurrentTime(a.currentTime);
            setProgress(a.duration > 0 ? a.currentTime / a.duration : 0);
          }}
          style={{ display: 'none' }}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------
// Composer bar (bottom)
// ---------------------------------------------------------------------

function ComposerBar({
  prompt,
  setPrompt,
  lyricsMode,
  onToggleLyrics,
  lyrics,
  setLyrics,
  instrumental,
  onToggleInstrumental,
  language,
  onCycleLanguage,
  referenceLabel,
  onClearReference,
  onOpenReference,
  preset,
  onClearPreset,
  onOpenPresets,
  canSend,
  onGenerate,
  generating,
  disabled,
}: {
  prompt: string;
  setPrompt: (v: string) => void;
  lyricsMode: boolean;
  onToggleLyrics: () => void;
  lyrics: string;
  setLyrics: (v: string) => void;
  instrumental: boolean;
  onToggleInstrumental: () => void;
  language: 'en' | 'es' | 'pt';
  onCycleLanguage: () => void;
  referenceLabel: string | null;
  onClearReference: () => void;
  onOpenReference: () => void;
  preset: { name: string } | null;
  onClearPreset: () => void;
  onOpenPresets: () => void;
  canSend: boolean;
  onGenerate: () => void;
  generating: boolean;
  disabled: boolean;
}) {
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  const lyricsRef = useRef<HTMLTextAreaElement | null>(null);
  return (
    <Box
      bg="mg.surface"
      borderTop="1px solid"
      borderColor="mg.line"
      px={3}
      pt={2.5}
      pb={3.5}
      display="flex"
      flexDirection="column"
      gap={2}
    >
      {/* Reference row */}
      <Flex align="center" gap={1.5}>
        {referenceLabel ? (
          <Chip tone="on" onClick={onOpenReference}>
            <ChipIcon>🎧</ChipIcon>
            <Text as="span" noOfLines={1} maxW="140px">{referenceLabel}</Text>
            <ChipClear onClick={(e) => { e.stopPropagation(); onClearReference(); }} />
          </Chip>
        ) : (
          <Chip onClick={onOpenReference}>
            <ChipIcon>＋</ChipIcon>
            <Text as="span">Reference</Text>
          </Chip>
        )}
        <Text ml="auto" fontSize="10.5px" color="mg.dim" textTransform="uppercase" letterSpacing="0.10em" fontWeight={700}>
          {referenceLabel ? '1 / 1' : '0 / 1'}
        </Text>
      </Flex>

      {/* Prompt bar — textarea + send button. Length pill removed: the
          backend picks the length based on the model + instrumental flag,
          exposing a UI toggle here would be fake control. */}
      <Box
        display="grid"
        gridTemplateColumns="1fr auto"
        gap={2}
        alignItems="end"
        bg="mg.surfaceHi"
        border="1px solid"
        borderColor={canSend ? '#ec4899' : 'mg.line2'}
        borderRadius="16px"
        p="8px 8px 8px 12px"
        transition="border-color .15s"
      >
        <Textarea
          ref={promptRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder='Describe your song — e.g. "melancholic indie pop about a rainy Sunday morning"'
          rows={1}
          maxLength={2000}
          resize="none"
          border={0}
          bg="transparent"
          color="mg.fg"
          fontSize="13.5px"
          lineHeight={1.4}
          minH="38px"
          p="8px 0 4px"
          _placeholder={{ color: 'mg.dim' }}
          _focusVisible={{ boxShadow: 'none' }}
          sx={{ '&:focus': { outline: 'none' } }}
          disabled={disabled}
        />
        <Box
          as="button"
          w="44px"
          h="44px"
          borderRadius="full"
          bg={canSend ? MG_GRADIENT : 'mg.surfaceHi'}
          color={canSend ? 'white' : 'mg.dim'}
          border={0}
          display="grid"
          placeItems="center"
          boxShadow={canSend ? '0 8px 20px -6px rgba(236,72,153,0.55)' : 'none'}
          position="relative"
          fontSize="20px"
          lineHeight={1}
          onClick={onGenerate}
          disabled={!canSend || generating}
          aria-label="Generate"
          cursor={canSend && !generating ? 'pointer' : 'default'}
        >
          {generating ? '…' : '↑'}
          {canSend && !generating && (
            <Box
              position="absolute"
              bottom="-4px"
              right="-4px"
              bg="mg.fg"
              color="#7c2d12"
              fontSize="9.5px"
              fontWeight={800}
              borderRadius="full"
              px="5px"
              py="1px"
              boxShadow="0 2px 4px rgba(0,0,0,0.15)"
              letterSpacing={0}
            >
              1
            </Box>
          )}
        </Box>
      </Box>

      {/* Optional lyrics textarea — appears when the + Lyrics chip is on.
          Kept intentionally spare: no formatting toolbar, no structure
          hints. Just a place to paste. */}
      {lyricsMode && (
        <Textarea
          ref={lyricsRef}
          value={lyrics}
          onChange={(e) => setLyrics(e.target.value)}
          placeholder="Paste your lyrics here (optional). Leave blank to have MixGenie write them."
          rows={4}
          maxLength={8000}
          resize="vertical"
          bg="mg.surfaceHi"
          border="1px solid"
          borderColor="mg.line2"
          color="mg.fg"
          fontSize="13.5px"
          lineHeight={1.45}
          borderRadius="14px"
          p="10px 12px"
          _placeholder={{ color: 'mg.dim' }}
          disabled={disabled}
        />
      )}

      {/* Sub-row: Presets pill · Lyrics chip · Language chip · Instrumental toggle · price */}
      <Flex align="center" gap={2} px="2px" flexWrap="wrap">
        {preset ? (
          <Chip tone="on" onClick={onOpenPresets}>
            <ChipIcon>✨</ChipIcon>
            <Text as="span" noOfLines={1} maxW="140px">{preset.name}</Text>
            <ChipClear onClick={(e) => { e.stopPropagation(); onClearPreset(); }} />
          </Chip>
        ) : (
          <Chip onClick={onOpenPresets}>
            <ChipIcon>✨</ChipIcon>
            <Text as="span">Presets</Text>
          </Chip>
        )}
        {!instrumental && (
          <Chip tone={lyricsMode ? 'on' : 'default'} onClick={onToggleLyrics}>
            <ChipIcon>✍️</ChipIcon>
            <Text as="span">{lyricsMode ? 'Lyrics' : '+ Lyrics'}</Text>
          </Chip>
        )}
        {!instrumental && (
          <Chip onClick={onCycleLanguage}>
            <ChipIcon>{language.toUpperCase()}</ChipIcon>
            <Text as="span">Language</Text>
          </Chip>
        )}
        <Chip tone={instrumental ? 'on' : 'default'} onClick={onToggleInstrumental}>
          <ChipIcon>🎹</ChipIcon>
          <Text as="span">Instrumental</Text>
        </Chip>
        <Text
          ml="auto"
          fontSize="10.5px"
          color={generating ? 'hot.500' : 'mg.dim'}
          textTransform="uppercase"
          letterSpacing="0.10em"
          fontWeight={700}
        >
          {generating ? 'Generating…' : '1 credit per song'}
        </Text>
      </Flex>
    </Box>
  );
}

// -----------------------------------------------------------------------
// PresetsSheet — bottom sheet with the real preset catalog.
//
// Reuses useListPresetsQuery (the same endpoint the old GeneratorPage's
// PresetPicker consumes). Filters to the "sound" target because the
// composer's Presets pill drives the *style* of the song — lyric themes
// are a different picker that we'll surface on the Lyrics chip in P5.
// -----------------------------------------------------------------------

function PresetsSheet({
  isOpen,
  onClose,
  onPick,
  picked,
}: {
  isOpen: boolean;
  onClose: () => void;
  onPick: (preset: PresetDoc) => void;
  picked: PresetDoc | null;
}) {
  const { data, isLoading } = useListPresetsQuery(undefined as any, { skip: !isOpen });
  const [category, setCategory] = useState<string>('all');

  const soundGroups = useMemo(() => (data?.groups || []).filter((g) => g.target === 'sound'), [data]);
  const categories = useMemo(() => ['all', ...Array.from(new Set(soundGroups.map((g) => g.category)))], [soundGroups]);
  const presets = useMemo(() => {
    if (category === 'all') return soundGroups.flatMap((g) => g.presets);
    return soundGroups.filter((g) => g.category === category).flatMap((g) => g.presets);
  }, [soundGroups, category]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" size="lg" autoFocus={false}>
      <DrawerOverlay bg="blackAlpha.500" />
      <DrawerContent bg="mg.surface" color="mg.fg" borderTopRadius="18px" maxH="82vh">
        <Box textAlign="center" pt={3}>
          <Box mx="auto" w="40px" h="4px" bg="mg.dim" borderRadius="2px" />
        </Box>
        <DrawerHeader
          fontFamily="'Iowan Old Style', Charter, Georgia, serif"
          fontWeight={500}
          fontSize="18px"
          letterSpacing="-0.01em"
          pt={2}
          pb={2}
        >
          Presets — pick one &amp; play
        </DrawerHeader>
        <DrawerBody px={3} pb={5} display="flex" flexDirection="column" gap={3}>
          {/* Category pills */}
          <Flex gap={1.5} overflowX="auto" pb={1}>
            {categories.map((c) => (
              <Box
                key={c}
                as="button"
                flex="0 0 auto"
                px={3}
                py={1.5}
                bg={category === c ? MG_GRADIENT : 'mg.surfaceHi'}
                color={category === c ? 'white' : 'mg.muted'}
                border="1px solid"
                borderColor={category === c ? 'transparent' : 'mg.line'}
                borderRadius="full"
                fontSize="11.5px"
                fontWeight={600}
                textTransform="capitalize"
                onClick={() => setCategory(c)}
              >
                {c}
              </Box>
            ))}
          </Flex>

          {/* Grid */}
          {isLoading ? (
            <Flex align="center" justify="center" py={10}><Spinner color="hot.500" /></Flex>
          ) : (
            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2.5} overflowY="auto">
              {presets.map((p: any) => {
                const isPicked = picked && String(picked._id) === String(p._id);
                return (
                  <Box
                    key={String(p._id)}
                    as="button"
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    textAlign="left"
                    onClick={() => onPick(p)}
                  >
                    <Box
                      w="100%"
                      style={{ aspectRatio: '1' }}
                      borderRadius="10px"
                      bg={p.coverImageUrl ? `url(${p.coverImageUrl}) center/cover` : MG_GRADIENT}
                      boxShadow={isPicked ? '0 0 0 2px #ec4899 inset' : 'none'}
                      position="relative"
                    >
                      {isPicked && (
                        <Box
                          position="absolute" top={1.5} right={1.5}
                          w="20px" h="20px"
                          bg="white" color="hot.500"
                          borderRadius="full"
                          display="grid" placeItems="center"
                          fontSize="11px" fontWeight={800}
                        >
                          ✓
                        </Box>
                      )}
                    </Box>
                    <Text fontSize="11.5px" fontWeight={600} color="mg.fg" noOfLines={1}>{p.name}</Text>
                    <Text fontSize="10px" color="mg.muted" noOfLines={1}>{p.category}</Text>
                  </Box>
                );
              })}
              {presets.length === 0 && !isLoading && (
                <Text gridColumn="1 / -1" fontSize="13px" color="mg.muted" py={6} textAlign="center">
                  No presets found in this category.
                </Text>
              )}
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function Chip({ children, onClick, tone = 'default' }: {
  children: React.ReactNode;
  onClick?: () => void;
  tone?: 'default' | 'on';
}) {
  const isOn = tone === 'on';
  return (
    <Box
      as="button"
      display="inline-flex"
      alignItems="center"
      h="32px"
      pl="5px"
      pr="12px"
      bg={isOn ? 'rgba(168,85,247,0.10)' : 'mg.surfaceHi'}
      border="1px solid"
      borderColor={isOn ? 'rgba(168,85,247,0.30)' : 'mg.line2'}
      borderRadius="full"
      fontSize="12px"
      color="mg.fg"
      fontWeight={600}
      gap={1.5}
      onClick={onClick}
    >
      {children}
    </Box>
  );
}

function ChipIcon({ children }: { children: React.ReactNode }) {
  return (
    <Box
      w="22px"
      h="22px"
      borderRadius="6px"
      bg="rgba(236,72,153,0.12)"
      color="hot.500"
      display="grid"
      placeItems="center"
      fontSize="12px"
    >
      {children}
    </Box>
  );
}

function ChipClear({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <Box
      as="span"
      color="mg.dim"
      fontSize="11px"
      pl="4px"
      borderLeft="1px solid"
      borderColor="mg.line2"
      ml="4px"
      cursor="pointer"
      onClick={onClick as any}
    >
      ✕
    </Box>
  );
}

// ---------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------

type Reference = {
  title: string;
  artist: string;
  slug: string;
  artistSlug: string;
  cover: string;
  styleNotes: string;
};

export default function ComposerPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { authenticated, user, logout } = useAuth();
  const authState = useSelector((s: RootState) => s.auth);
  const isAuthed = !!authState?.authenticated || authenticated;
  const drawer = useDisclosure();
  const settings = useDisclosure();
  const presetsSheet = useDisclosure();
  // Paywall modal — opens on any 402 / INSUFFICIENT_CREDITS response from
  // song-gen, stem-split, stems-from-upload. Same pictools mechanism:
  // one shared modal, "See plans" pushes to /credits.
  const paywall = useDisclosure();

  // Credits — only queried when we actually have a session.
  const { data: creditsData, refetch: refetchCredits } = useGetCreditsBalanceQuery(undefined as any, { skip: !isAuthed });
  const credits = isAuthed && creditsData && 'balance' in (creditsData as any) ? (creditsData as any).balance : null;

  // ------------------------------ Composer state ------------------------------
  const [prompt, setPrompt] = useState<string>(params.get('prompt') || params.get('vibe') || '');
  const [lyricsMode, setLyricsMode] = useState<boolean>(false);
  const [lyrics, setLyrics] = useState<string>('');
  const [instrumental, setInstrumental] = useState<boolean>(false);
  const [language, setLanguage] = useState<'en' | 'es' | 'pt'>('en');
  const [preset, setPreset] = useState<PresetDoc | null>(null);

  // Reference can arrive two ways:
  //   1. Catalog reference (title + artist + slugs + cover) — from DLC hand-off
  //      via `?referenceTitle=`. Generator's `referenceMode: 'title-artist'`.
  //   2. Local audio file (uploaded here). Overrides catalog if both present;
  //      backend generator's `referenceMode: 'audio-snippet'`.
  const initialRef = useMemo<Reference | null>(() => {
    const title = params.get('referenceTitle') || '';
    const artist = params.get('referenceArtist') || '';
    if (!title && !artist) return null;
    return {
      title,
      artist,
      slug: params.get('referenceSlug') || '',
      artistSlug: params.get('referenceArtistSlug') || '',
      cover: params.get('referenceCover') || '',
      styleNotes: params.get('styleNotes') || '',
    };
  }, [params]);
  const [reference, setReference] = useState<Reference | null>(initialRef);
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const referenceInputRef = useRef<HTMLInputElement | null>(null);
  const stemsInputRef = useRef<HTMLInputElement | null>(null);
  // Carries the split depth the user picked in the DropCard's menu into
  // the file-picker onChange (which knows nothing about the menu choice).
  // Default matches the "full" option so a stray call without a picked
  // model still gives the richest split.
  const pendingStemModelRef = useRef<'audio-separation-1' | 'audio-separation-2' | 'audio-separation-3'>('audio-separation-2');

  // Job lifecycle. Once currentJobId is set we poll every 3s until the job
  // reaches a terminal status (completed | failed). The `pollingInterval:
  // 0` when idle stops RTK Query polling entirely between generations.
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // `isSubmitting` from RTK covers the network gap between the user
  // tapping Generate and the createJob mutation resolving with a job id.
  // Before this was in place, `generating` only flipped true once the
  // mutation returned — the ~10s round-trip left the send button live and
  // a double-tap enqueued a second job (which hit Mureka's concurrent=1
  // limit and failed with 429). Rolling isSubmitting into `generating`
  // disables the button AND swaps the workspace to the "composing"
  // overlay from the very first tap.
  const [createJob, { isLoading: isSubmitting }] = useCreateJobMutation();
  const [stemsFromUpload] = useStemsFromUploadMutation();
  const [fetchEmbedPending] = useLazyGetEmbedPendingQuery();
  const { data: jobData } = useGetJobQuery(currentJobId!, {
    skip: !currentJobId,
    pollingInterval: currentJobId ? 3000 : 0,
  });
  const currentJob = jobData?.data;
  const isJobComplete = currentJob?.status === 'completed' || currentJob?.status === 'failed';
  const generating = isSubmitting || (!!currentJobId && !isJobComplete);

  // Terminal-status effects: refresh credits + surface a toast on failure.
  useEffect(() => {
    if (!currentJob || !isJobComplete) return;
    if (currentJob.status === 'failed') {
      toast({ title: 'Generation failed', description: currentJob.errorMessage || 'Try a different prompt or reference.', status: 'error' });
    } else {
      refetchCredits();
    }
  }, [currentJob?._id, currentJob?.status, isJobComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive the visible stage from the job's status. Empty stage when no
  // job is active; generating while polling; result when completed.
  const view: ViewMode = generating ? 'generating'
    : currentJob?.status === 'completed' ? 'result'
    : 'empty';

  // Scroll to top on stage change so the reveal is visible.
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { workspaceRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }, [view]);

  // ------------------------------ Pending hydration ---------------------------
  // Widget hand-off from mixgenie.ai / dicelacancion.com lands at
  // /composer?pending=<id>. Fetch the payload, prefill the composer,
  // strip the query so a refresh doesn't re-hydrate.
  const hydratedPendingRef = useRef<string | null>(null);
  useEffect(() => {
    const pendingId = params.get('pending');
    if (!pendingId || hydratedPendingRef.current === pendingId) return;
    hydratedPendingRef.current = pendingId;
    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchEmbedPending(pendingId).unwrap();
        if (cancelled || !payload) return;
        if (payload.prompt) setPrompt(payload.prompt);
        if (payload.lyrics) { setLyrics(payload.lyrics); setLyricsMode(true); }
        if (payload.instrumental) setInstrumental(true);
        if (payload.language && ['en', 'es', 'pt'].includes(payload.language)) {
          setLanguage(payload.language as 'en' | 'es' | 'pt');
        }
        if (payload.mode === 'reference' && (payload.referenceSongTitle || payload.referenceArtistName)) {
          setReference({
            title: payload.referenceSongTitle || '',
            artist: payload.referenceArtistName || '',
            slug: payload.referenceSongSlug || '',
            artistSlug: payload.referenceArtistSlug || '',
            cover: payload.referenceCoverUrl || '',
            styleNotes: '',
          });
        }
        const next = new URLSearchParams(params);
        next.delete('pending');
        setParams(next, { replace: true });
      } catch { /* TTL expired or transient — silent */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // ------------------------------ Handlers ------------------------------------
  function cycleLanguage() {
    setLanguage((l) => (l === 'en' ? 'es' : l === 'es' ? 'pt' : 'en'));
  }
  function toggleInstrumental() {
    setInstrumental((v) => {
      const next = !v;
      if (next) setLyricsMode(false); // Instrumental + lyrics is nonsense.
      return next;
    });
  }
  function toggleLyricsMode() {
    setLyricsMode((v) => {
      const next = !v;
      if (next) setInstrumental(false);
      return next;
    });
  }
  function onPickReferenceFile() { referenceInputRef.current?.click(); }

  // "Split stems" flow — user picks a full audio file, we upload it and
  // create an upload-only stem job on the BE, then navigate to /studio/:id
  // where the mixer takes over. Anon → bounce through login and remember
  // the intent via ?nextAction=stems so we can prompt them to re-pick on
  // return (files can't survive the OAuth redirect).
  //
  // stemModel is captured from the DropCard menu click and stashed on a
  // ref so the file input's onChange (below) can read it after the native
  // dialog closes — the change event doesn't carry any of our own state.
  function onPickStemsFile(stemModel: 'audio-separation-1' | 'audio-separation-2' | 'audio-separation-3' = 'audio-separation-2') {
    if (!isAuthed) {
      navigate('/login?next=/composer%3FnextAction%3Dstems');
      return;
    }
    pendingStemModelRef.current = stemModel;
    stemsInputRef.current?.click();
  }
  async function onStemsFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!isAuthed) {
      navigate('/login?next=/composer');
      return;
    }
    // 25 MB cap matches the BE (server rejects larger). Reading as base64
    // roughly 33% inflates in transit; well under the 20 MB body-parser cap
    // for typical 3-5 min songs.
    if (file.size > 25 * 1024 * 1024) {
      setError('That file is too large — please trim to under 25 MB before splitting.');
      return;
    }
    setError(null);
    try {
      // Reuse the existing reference-audio helper — same shape (base64 body
       // + fileName/mimeType) is what the stems-from-upload BE endpoint wants.
      const packaged = await fileToReferenceAudio(file);
      const audioBase64 = packaged.base64;
      const result = await stemsFromUpload({
        audioBase64,
        fileName: file.name,
        mimeType: file.type || 'audio/mpeg',
        stemModel: pendingStemModelRef.current,
      }).unwrap();
      const newJobId = result?.data?._id || result?.data?.id;
      if (newJobId) navigate(`/studio/${newJobId}`);
      else setError('Split kicked off but no job id came back — check My songs.');
    } catch (err: any) {
      if (err?.data?.code === 'INSUFFICIENT_CREDITS' || err?.status === 402) {
        // Pictools pattern: pop the paywall modal instead of an inline
        // toast — it converts better and gives one clear next action.
        paywall.onOpen();
      } else {
        setError(err?.data?.message || 'Could not start stem separation. Please try again.');
      }
    }
  }
  function onReferenceFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 32 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Reference audio must be under 32 MB.', status: 'warning' });
      return;
    }
    setReferenceFile(f);
    setReference(null); // Local file overrides catalog reference.
  }
  function clearReference() { setReference(null); setReferenceFile(null); }

  const canSend = !generating && (
    prompt.trim().length > 0
    || lyrics.trim().length > 0
    || !!preset
    || !!reference
    || !!referenceFile
  );

  const lastSubmitRef = useRef<number>(0);
  // Synchronous in-flight lock — blocks re-entrant handleGenerate calls
  // during the ~10s network gap before RTK's `isSubmitting` propagates
  // through render. Real bug hit in prod: user tapped Generate twice
  // because the button hadn't visually disabled yet + no overlay was
  // showing, resulting in a second job that hit Mureka's concurrent=1
  // limit and failed. The ref flips synchronously at the top of the
  // handler; useState + RTK isLoading catch up on the next render.
  const submittingRef = useRef<boolean>(false);

  async function handleGenerate() {
    if (submittingRef.current) return;
    if (!canSend) return;
    // Anonymous → bounce through login. AuthCallbackPage restores the
    // localStorage-stashed pending id (if any) so the widget hand-off
    // survives; native login preserves ?next=.
    if (!isAuthed) {
      navigate('/login?next=/composer');
      return;
    }
    // Guard rage-taps outside a single mutation (e.g. clicking again
    // right after a failed job resolves). Mureka rate-limits per account
    // and 429s cost a round-trip even when rejected.
    const now = Date.now();
    if (now - lastSubmitRef.current < 3000) return;
    lastSubmitRef.current = now;
    submittingRef.current = true;
    setError(null);
    setCurrentJobId(null); // Clear the previous job so the stage swaps immediately.

    try {
      const referenceAudio = referenceFile ? await fileToReferenceAudio(referenceFile) : undefined;
      const composedStyle = [prompt.trim(), preset?.promptFragment].filter(Boolean).join(' — ');
      const vibe = composedStyle
        || (reference ? `Create an original song inspired by ${reference.title} by ${reference.artist}.` : '');
      const jobPayload: any = {
        email: user?.email,
        userId: user?.id || user?._id,
        vibe,
        prompt: composedStyle || undefined,
        userPromptText: prompt.trim() || undefined,
        instrumental,
        generateCover: true,
        lyrics: lyricsMode && lyrics.trim() ? lyrics.trim() : undefined,
        lyricsLanguage: !instrumental ? (LANG_NAMES[language] || undefined) : undefined,
        referenceMode: (referenceFile || reference) ? (referenceFile ? 'audio-snippet' : 'title-artist') : undefined,
        referenceTitle: reference?.title || undefined,
        referenceArtist: reference?.artist || undefined,
        referenceSlug: reference?.slug || undefined,
        referenceArtistSlug: reference?.artistSlug || undefined,
        styleNotes: reference?.styleNotes || undefined,
        referenceAudio,
      };

      const result = await createJob(jobPayload).unwrap();
      if (result?.code === 'INSUFFICIENT_CREDITS') {
        paywall.onOpen();
        return;
      }
      setCurrentJobId(result?.data?.id || result?.data?._id || null);
    } catch (err: any) {
      if (err?.data?.code === 'INSUFFICIENT_CREDITS' || err?.status === 402) {
        // Pictools pattern: pop the paywall modal instead of an inline
        // toast — it converts better and gives one clear next action.
        paywall.onOpen();
      } else if (err?.data?.code === 'QUEUE_FULL_UPGRADE' || err?.status === 429) {
        setError('Free-tier queue is full. Try again shortly or upgrade for parallel generations.');
      } else {
        setError(err?.data?.message || 'Generation failed. Please try again.');
      }
    } finally {
      submittingRef.current = false;
    }
  }

  function generateAnother() {
    // Reset job state but keep prompt/preset/reference so a "variation"
    // is one tap away. Clearing chips is user-initiated.
    setCurrentJobId(null);
    setError(null);
  }

  function downloadCurrent() {
    const audio = currentJob?.outputAudioUrl || currentJob?.outputUrl;
    if (!audio) return;
    const a = document.createElement('a');
    a.href = audio;
    a.download = (currentJob?.title || 'song').replace(/[^\w\-. ()]+/g, '_').slice(0, 80) + '.mp3';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const referenceLabel = referenceFile
    ? referenceFile.name
    : reference
      ? `${reference.title || 'Reference'}${reference.artist ? ` · ${reference.artist}` : ''}`
      : null;

  return (
    <Flex direction="column" bg="mg.bg" color="mg.fg" minH="100dvh" maxH="100dvh">
      <TopBar
        authenticated={isAuthed}
        credits={credits}
        onSignInClick={() => navigate('/login?next=/composer')}
        onMenuClick={drawer.onOpen}
      />
      <NavDrawer
        isOpen={drawer.isOpen}
        onClose={drawer.onClose}
        authenticated={isAuthed}
        email={user?.email || ''}
        credits={credits}
        onSignOut={async () => { await logout(); navigate('/composer'); }}
        onOpenSettings={() => { drawer.onClose(); settings.onOpen(); }}
      />
      <SettingsSheet
        isOpen={settings.isOpen}
        onClose={settings.onClose}
        authenticated={isAuthed}
        onSignOut={async () => { await logout(); navigate('/composer'); }}
      />
      <PresetsSheet
        isOpen={presetsSheet.isOpen}
        onClose={presetsSheet.onClose}
        onPick={(p) => { setPreset(p); presetsSheet.onClose(); }}
        picked={preset}
      />

      {/* Paywall — pictools mechanism: single shared modal that opens
          whenever the BE returns 402 / INSUFFICIENT_CREDITS on song-gen,
          stem-split, or stems-from-upload. "See plans" pushes to /credits
          where the buy-out / subscription UI already lives. */}
      <Modal isOpen={paywall.isOpen} onClose={paywall.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="mg.surface" color="mg.fg" borderRadius="16px" mx={4}>
          <ModalHeader fontSize="lg" fontWeight={700}>You're out of credits</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="mg.muted" mb={3}>
              Top up to keep generating new songs, remixes and stem splits.
            </Text>
            <Box as="ul" pl={4} fontSize="sm" color="mg.fg" sx={{ listStyle: 'disc' }}>
              <li>Credit packs — pay once, use whenever</li>
              <li>Monthly plans with priority generation</li>
              <li>MIDI + 12-stem splits included on paid tiers</li>
            </Box>
          </ModalBody>
          <ModalFooter gap={2}>
            <Box
              as="button"
              onClick={paywall.onClose}
              px={4} py={2}
              borderRadius="full" fontSize="sm" fontWeight={600}
              bg="transparent" color="mg.muted"
              _hover={{ color: 'mg.fg' }}
            >
              Not now
            </Box>
            <Box
              as="button"
              onClick={() => { paywall.onClose(); navigate('/credits'); }}
              px={5} py={2}
              borderRadius="full" fontSize="sm" fontWeight={700}
              bg="hot.500" color="white"
              _hover={{ bg: 'hot.600' }}
            >
              See plans →
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Hidden reference-audio file input — triggered by the drop card
          and the composer bar's Reference chip. */}
      <Input
        ref={referenceInputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/wav,audio/*"
        display="none"
        onChange={onReferenceFileChosen}
      />
      {/* Hidden stem-split file input — triggered by the DropCard's
          "Split stems" tile. Different from the reference-audio input:
          the user picks a full song here, we upload it, create a synthetic
          stem-only job on the BE, and navigate straight to /studio/:jobId. */}
      <Input
        ref={stemsInputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/wav,audio/*"
        display="none"
        onChange={onStemsFileChosen}
      />

      {/* Workspace */}
      <Box
        ref={workspaceRef}
        flex="1 1 auto"
        overflow="auto"
        p={4}
        bg="mg.surfaceLo"
        position="relative"
        sx={{
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(500px 240px at 50% 0%, rgba(236,72,153,0.06), transparent 60%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Box position="relative" zIndex={1} maxW="640px" mx="auto">
          {view === 'empty' && (
            <DropCard
              onTypeIdea={() => { /* prompt is already the primary focus */ }}
              onUploadReference={onPickReferenceFile}
              onPickPreset={presetsSheet.onOpen}
              onSplitStems={onPickStemsFile}
            />
          )}
          {view === 'generating' && <GeneratingStage />}
          {view === 'result' && currentJob && (
            <ResultStage
              title={currentJob.title || 'Your song'}
              style={preset?.name || (currentJob.style as string) || 'MixGenie'}
              coverImageUrl={currentJob.coverImageUrl || currentJob.cover}
              audioUrl={currentJob.outputAudioUrl || currentJob.outputUrl}
              onGenerateAnother={generateAnother}
              onDownload={downloadCurrent}
              onOpenStudio={
                currentJob._id
                  ? () => navigate(`/studio/${currentJob._id}`)
                  : undefined
              }
            />
          )}
          {error && (
            <Box mt={3} px={3} py={2.5} bg="red.500" color="white" borderRadius="12px" fontSize="13px" fontWeight={600}>
              {error}
            </Box>
          )}
        </Box>
      </Box>

      <ComposerBar
        prompt={prompt}
        setPrompt={setPrompt}
        lyricsMode={lyricsMode}
        onToggleLyrics={toggleLyricsMode}
        lyrics={lyrics}
        setLyrics={setLyrics}
        instrumental={instrumental}
        onToggleInstrumental={toggleInstrumental}
        language={language}
        onCycleLanguage={cycleLanguage}
        referenceLabel={referenceLabel}
        onClearReference={clearReference}
        onOpenReference={onPickReferenceFile}
        preset={preset}
        onClearPreset={() => setPreset(null)}
        onOpenPresets={presetsSheet.onOpen}
        canSend={canSend}
        onGenerate={handleGenerate}
        generating={generating}
        disabled={generating}
      />
    </Flex>
  );
}
